import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Plane, Ship, Truck } from 'lucide-react';

const ServicesPreview = () => {
  const { t, language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const servicesPreview = [
    {
      id: 1,
      title: 'Transporte',
      subtitle: { pt: 'aéreo', en: 'air', es: 'aéreo' },
      icon: Plane,
      image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=600&q=80',
      link: '/transporte-aereo',
    },
    {
      id: 2,
      title: 'Transporte',
      subtitle: { pt: 'marítimo', en: 'sea', es: 'marítimo' },
      icon: Ship,
      image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=600&q=80',
      link: '/transporte-maritimo',
    },
    {
      id: 3,
      title: 'Transporte',
      subtitle: { pt: 'rodoviário', en: 'road', es: 'terrestre' },
      icon: Truck,
      image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=600&q=80',
      link: '/transporte-rodoviario',
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-gray-50 dark:bg-gray-800 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`flex flex-col md:flex-row md:items-center md:justify-between mb-12 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 md:mb-0">
            {t('services.title')}
          </h2>
          <Link
            to="/servicos"
            className="text-[#3ECDA3] font-medium text-sm hover:underline flex items-center gap-2 group"
          >
            {t('services.viewAll')}
            <span className="transform group-hover:translate-x-2 transition-transform duration-300">→</span>
          </Link>
        </div>

        {/* Services Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {servicesPreview.map((service, index) => {
            const IconComponent = service.icon;
            const CardWrapper = service.link ? Link : 'div';
            const cardProps = service.link ? { to: service.link } : {};
            
            return (
              <CardWrapper
                key={service.id}
                {...cardProps}
                className={`group relative h-[300px] md:h-[400px] rounded-xl overflow-hidden transition-all duration-700 block ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                {/* Background Image */}
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{ backgroundImage: `url('${service.image}')` }}
                >
                  <div className="absolute inset-0 bg-black/50 group-hover:bg-black/60 transition-colors duration-300"></div>
                </div>

                {/* Content */}
                <div className="relative z-10 h-full flex flex-col justify-end p-6">
                  <div className="transform group-hover:-translate-y-2 transition-transform duration-300">
                    <h3 className="text-white text-xl md:text-2xl font-light">
                      {service.title}
                      <span className="block text-[#3ECDA3] font-semibold ml-4">
                        {service.subtitle[language]}
                      </span>
                    </h3>
                    {service.link && (
                      <span className="inline-block mt-3 text-white/80 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        Saiba mais →
                      </span>
                    )}
                  </div>
                  
                  {/* Hover Icon */}
                  <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0">
                    <div className="bg-[#3ECDA3] p-3 rounded-full">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
              </CardWrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesPreview;
